import { EntityDetailSkeleton } from '@/components/layout/entity-detail-skeleton'
import { Briefcase, MapPin } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { CopyPublicLinkButton } from '@/components/public/copy-public-link-button'
import { PublicEntityHeader } from '@/components/public/public-entity-header'
import { PUBLIC_ENTITY_IDENTITY_OVERLAP_CLASS } from '@/components/public/public-entity-hero'
import { PublicEntityMetadataRow } from '@/components/public/public-entity-metadata-row'
import { PublicPageHead } from '@/components/public/public-page-head'
import { PublicShareLayout } from '@/components/public/public-share-layout'
import { PersonProfileStandingSections } from '@/components/profile/person-profile-standing-view'
import { OpenToWorkBadge } from '@/components/profile/open-to-work-badge'
import { PUBLIC_BANNER_SURFACE, PUBLIC_CARD_SURFACE, PUBLIC_IMAGE_OUTLINE } from '@/components/public/public-surfaces'
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { PublicContactStrip } from '@/components/ui/entity-contact-sections'
import { cn } from '@/lib/utils'
import { extraSocialContactFrom } from '@/lib/social-networks'
import { InitialsAvatar } from '@/components/ui/initials-avatar'
import { TenantLogoWatermark } from '@/components/ui/tenant-logo-watermark'
import { API_ORIGIN } from '@/constants'
import { publicProfileToStandingData } from '@/lib/public-profile-standing'
import { usePublicShareRouteParam } from '@/lib/public-share-route-param'
import { formatEntityLocation } from '@/utils/format-entity-location'
import type { PublicProfile } from '@/types/public-share'
import { getProfileInitials } from '@/components/profile/profile-initials'

export function PublicProfilePage({ slugOverride }: { slugOverride?: string } = {}) {
  const slug = usePublicShareRouteParam('/p/:slug', 'slug', slugOverride)
  const [profile, setProfile] = useState<PublicProfile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!slug) {
        if (!cancelled) {
          setLoading(false)
          setError('Profile not found')
        }
        return
      }
      setLoading(true)
      setError(null)
      try {
        const response = await fetch(`${API_ORIGIN}/api/public/profiles/${encodeURIComponent(slug)}`)
        const raw = await response.text()
        let data: Record<string, unknown> = {}
        try {
          data = raw ? (JSON.parse(raw) as Record<string, unknown>) : {}
        } catch {
          throw new Error('Profile could not be loaded.')
        }
        if (!response.ok) {
          throw new Error(typeof data.error === 'string' ? data.error : 'Profile not found')
        }
        if (!cancelled) setProfile(data as unknown as PublicProfile)
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Profile not found')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [slug])

  const standingProfile = useMemo(
    () => (profile ? publicProfileToStandingData(profile) : null),
    [profile],
  )

  if (loading) {
    return (
      <PublicShareLayout>
        <EntityDetailSkeleton variant="profile" withPageContainer={false} ariaLabel="Loading profile" />
      </PublicShareLayout>
    )
  }

  if (error || !profile || !standingProfile) {
    return (
      <PublicShareLayout>
        <Card className={PUBLIC_CARD_SURFACE}>
          <CardHeader>
            <CardTitle>Profile unavailable</CardTitle>
            <CardDescription alwaysShow>{error || 'This profile could not be loaded.'}</CardDescription>
          </CardHeader>
        </Card>
      </PublicShareLayout>
    )
  }

  const locationLine = formatEntityLocation(profile.city, profile.country)
  const publicUrl = profile.public_url || `${window.location.origin}/p/${profile.slug}`
  const displayName = profile.full_name || 'Profile'
  const { colorSeed: profileColorSeed } = getProfileInitials({
    full_name: profile.full_name ?? undefined,
  })

  const identityHeader = (
    <PublicEntityHeader
      clearance="heroAvatar"
      density="compact"
      title={
        <>
          <h1 className="mb-3 flex flex-wrap items-center gap-2 text-3xl font-semibold tracking-tight">
            <span className="min-w-0">{displayName}</span>
            {profile.extensions?.candidate?.open_to_work ? <OpenToWorkBadge /> : null}
          </h1>
          {profile.headline ? (
            <PublicEntityMetadataRow icon={Briefcase}>{profile.headline}</PublicEntityMetadataRow>
          ) : null}
        </>
      }
      actions={<CopyPublicLinkButton url={publicUrl} />}
    >
      {locationLine ? (
        <PublicEntityMetadataRow icon={MapPin}>{locationLine}</PublicEntityMetadataRow>
      ) : null}
      <PublicContactStrip
        website={profile.website}
        linkedin_url={profile.linkedin_url}
        x_url={profile.x_url}
        {...extraSocialContactFrom(profile)}
      />
    </PublicEntityHeader>
  )

  return (
    <PublicShareLayout
      hero={
        <section className="relative">
          <div className={PUBLIC_BANNER_SURFACE}>
            <div className="relative flex h-[14.4rem] w-full md:h-[17.28rem]">
              {profile.banner_url ? (
                <img
                  src={profile.banner_url}
                  alt=""
                  className={cn('h-full w-full object-cover', PUBLIC_IMAGE_OUTLINE)}
                />
              ) : (
                <TenantLogoWatermark />
              )}
            </div>
          </div>
          <div className={PUBLIC_ENTITY_IDENTITY_OVERLAP_CLASS}>
            <InitialsAvatar
              src={profile.avatar_url}
              colorSeed={profileColorSeed || profile.slug}
              framed
              className="size-[108px] md:size-[138px]"
              alt=""
            />
          </div>
        </section>
      }
    >
      <PublicPageHead
        title={displayName}
        description={profile.headline || profile.bio?.slice(0, 200) || undefined}
        imageUrl={profile.avatar_url}
        canonicalPath={`/p/${profile.slug}`}
      />
      <div className="space-y-8">
        {identityHeader}
        <PersonProfileStandingSections profile={standingProfile} skillsVariant="public" />
      </div>
    </PublicShareLayout>
  )
}
